import { IS_BIZ_USER } from 'reactApp/appHelpers/configHelpers';
import {
    IS_ALL_DOC_SHARED_SORT_ENABLED,
    IS_ALL_DOC_SORT_ENABLED,
} from 'reactApp/appHelpers/featuresHelpers/features/alldocumentsSortEnabled';
import { getAllDocumentsCategory } from 'reactApp/modules/allDocuments/allDocuments.selectors';
import { AllDocumentsCategoryType } from 'reactApp/modules/allDocuments/allDocuments.types';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { type ISortState, ESortOder, ESortTypes } from 'reactApp/modules/sort/sort.types';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import type { RootState } from 'reactApp/store';
import type { Sort } from 'reactApp/types/Tree';
import { createSelector } from 'reselect';

export const defaultSort: Sort = {
    type: ESortTypes.name,
    order: ESortOder.asc,
};

export const defaultSortInAllDocs: Sort = {
    type: ESortTypes.mtime,
    order: ESortOder.desc,
};

const storages = [
    EStorageType.start,
    EStorageType.feed,
    EStorageType.albums,
    EStorageType.vigbo,
    ...(!IS_ALL_DOC_SORT_ENABLED ? [EStorageType.alldocuments] : []),
];

const getSortState = (state: RootState) => state.sort;

export const getSortById = createSelector(
    getSortState,
    getCurrentStorage,
    (state: RootState, id: string) => id,
    (state: ISortState, storage: EStorageType, id: string): Sort | undefined => {
        if (IS_BIZ_USER && storage === EStorageType.trashbin) {
            return state.sort[`${storage}${id}`];
        }

        return state.sort[id];
    }
);

export const getIsSortInitialized = createSelector(getSortState, (state: ISortState) => state.isInitialized);

export const getSort = createSelector(
    (state, _, storage?: EStorageType) => storage || getCurrentStorage(state),
    getCurrentStorage,
    (state, folder) => getSortById(state, folder?.id || ''),
    getAllDocumentsCategory,
    // eslint-disable-next-line complexity
    (storage, currentStorage, sort, currentCategory): Sort => {
        const noSortInCategory =
            IS_ALL_DOC_SORT_ENABLED &&
            !IS_ALL_DOC_SHARED_SORT_ENABLED &&
            (currentCategory === AllDocumentsCategoryType.sharedWithYou || currentCategory === AllDocumentsCategoryType.youShared);
        if (
            storages.includes(storage as EStorageType) ||
            (storage === EStorageType.gallery && !IS_BIZ_USER) ||
            currentStorage === EStorageType.start ||
            noSortInCategory
        ) {
            return {
                type: ESortTypes.mtime,
                order: ESortOder.desc,
            };
        }

        if (sort) {
            return sort;
        }

        if (storage === EStorageType.alldocuments || storage === EStorageType.incomingPublic) {
            return defaultSortInAllDocs;
        }

        if (IS_BIZ_USER && storage === EStorageType.trashbin) {
            return {
                type: ESortTypes.deletedAt,
                order: ESortOder.asc,
            };
        }

        if (
            (storage === EStorageType.trashbin && !IS_BIZ_USER) ||
            storage === EStorageType.search ||
            (storage === EStorageType.attaches && !IS_BIZ_USER) ||
            storage === EStorageType.documents
        ) {
            return {
                type: null,
            };
        }

        if (storage === EStorageType.sharedIncoming && !IS_BIZ_USER) {
            return {
                type: ESortTypes.unmountedFirst,
                order: ESortOder.asc,
            };
        }

        if (storage === EStorageType.sharedAutodelete) {
            return {
                type: ESortTypes.publicExpires,
                order: ESortOder.asc,
            };
        }

        if (storage === EStorageType.duplicates) {
            return {
                type: null,
            };
        }

        return defaultSort;
    }
);
